// Step 18 — AI Overview.
//
// Composes existing panels around the workspace resolved from
// /ai/context/'s active_workspace. No new backend endpoints introduced.
// Every card is either:
//   - a truthful "nothing here yet" empty state, OR
//   - a live panel that already existed for Steps 12-16.
//
// Explicitly does NOT invent broker KPIs. It surfaces work requiring
// attention (findings, risks, drafts, awaiting-approval).

import { Link } from "react-router-dom";

import ContractsPanel from "../ContractsPanel";
import DealActionsPanel from "../DealActionsPanel";
import DealSituationPanel from "../DealSituationPanel";
import NotificationsPanel from "../NotificationsPanel";

import {
  AIEmpty,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";

function KnownRisksCard({ dealIntel, activeWorkspaceId }) {
  const risks = dealIntel?.sections?.known_risks || {};
  const knownEntries = Object.entries(risks).filter(
    ([, v]) => v && v.known,
  );
  if (knownEntries.length === 0) {
    return (
      <AIEmpty
        title="Inga registrerade risker"
        hint="Fyll i riskprofilen på affären för att se signaler här."
      />
    );
  }
  return (
    <ul className="qai-overview-list">
      {knownEntries.slice(0, 5).map(([key, fact]) => (
        <li key={key} className="qai-overview-list-item">
          <span className="qai-overview-list-label">{key.replace(/_/g, " ")}</span>
          <span className="qai-overview-list-value">
            {String(fact.value ?? "—")}
          </span>
        </li>
      ))}
      {activeWorkspaceId && (
        <li className="qai-overview-list-more">
          <Link to={`/deals/${activeWorkspaceId}/risks`}>Se alla risker →</Link>
        </li>
      )}
    </ul>
  );
}

function RecentIntelligenceCard({ companyIntel, dealIntel, activeWorkspaceId }) {
  const companySections = companyIntel?.sections || {};
  const dealSections = dealIntel?.sections || {};

  // Pick a small set of "known" facts to surface — identity + financials
  // + valuation. These are already gated server-side by session.
  const rows = [];
  const identity = companySections.identity || {};
  if (identity.target_company_name?.known) {
    rows.push(["Målbolag", identity.target_company_name.value]);
  }
  if (companySections.financials?.revenue?.known) {
    rows.push(["Omsättning", companySections.financials.revenue.value]);
  }
  if (dealSections.valuation?.equity_value_mid?.known) {
    rows.push(["Värdering (mid)", dealSections.valuation.equity_value_mid.value]);
  }
  if (dealSections.process?.deal_stage?.known) {
    rows.push(["Steg", dealSections.process.deal_stage.value]);
  }

  if (rows.length === 0) {
    return (
      <AIEmpty
        title="Ingen intelligence tillgänglig ännu"
        hint="Kör in bokslut, riskprofil eller värdering för att fylla på."
      />
    );
  }

  return (
    <ul className="qai-overview-list">
      {rows.map(([label, val]) => (
        <li key={label} className="qai-overview-list-item">
          <span className="qai-overview-list-label">{label}</span>
          <span className="qai-overview-list-value">{String(val)}</span>
        </li>
      ))}
      {activeWorkspaceId && (
        <li className="qai-overview-list-more">
          <Link to={`/deals/${activeWorkspaceId}/intelligence`}>
            Öppna intelligence →
          </Link>
        </li>
      )}
    </ul>
  );
}

export default function AIOverview() {
  const {
    loading,
    activeWorkspace,
    activeWorkspaceId,
    companyIntel,
    dealIntel,
    workspaces,
  } = useAIWorkspace();

  if (loading) return <AILoading label="Laddar överblick…" />;

  if (!activeWorkspaceId) {
    return (
      <>
        <AIPageHeader
          title="Översikt"
          subtitle="Din AI-vy över alla pågående affärer."
        />
        <AIEmpty
          title="Ingen aktiv affär"
          hint={
            workspaces.length === 0
              ? "Du har inte kopplats till någon affär än."
              : "Välj en affär från listan för att komma igång."
          }
        >
          {workspaces.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Link to="/deals" className="qai-outreach-review-btn">
                Se affärer
              </Link>
            </div>
          )}
        </AIEmpty>
      </>
    );
  }

  return (
    <>
      <AIPageHeader
        title="Översikt"
        subtitle={activeWorkspace?.organisation_name}
        actions={
          activeWorkspaceId ? (
            <Link
              to={`/deals/${activeWorkspaceId}/intelligence`}
              className="qai-outreach-review-btn"
            >
              Öppna intelligence-vy
            </Link>
          ) : null
        }
      />

      <div className="qai-overview-grid">
        <AISection title="Senaste intelligence">
          <RecentIntelligenceCard
            companyIntel={companyIntel}
            dealIntel={dealIntel}
            activeWorkspaceId={activeWorkspaceId}
          />
        </AISection>

        <AISection title="Öppna risker">
          <KnownRisksCard
            dealIntel={dealIntel}
            activeWorkspaceId={activeWorkspaceId}
          />
        </AISection>

        <AISection title="Notiser">
          <NotificationsPanel workspaceId={activeWorkspaceId} />
        </AISection>

        <AISection title="Affärsläge">
          <DealSituationPanel workspaceId={activeWorkspaceId} />
        </AISection>

        <AISection title="Åtgärder att granska">
          <DealActionsPanel workspaceId={activeWorkspaceId} />
        </AISection>

        <AISection title="Avtalsutkast">
          <ContractsPanel workspaceId={activeWorkspaceId} />
        </AISection>
      </div>
    </>
  );
}
